import { useHandler } from "@redotech/react-util/hook";
import ShoppingCartOutlinedIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import { CartMessageType } from "@redotech/redo-model/conversation/message-parsers/cart-message-parser";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { ChatContentRenderer } from "@redotech/redo-web/concierge/chat-widget-components/chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "@redotech/redo-web/concierge/chat-widget-components/chat-content-states";
import { getConfirmedStateContent } from "@redotech/redo-web/concierge/chat-widget-components/tool-message-utils";
import { memo } from "react";
import * as addToCartButtonCss from "./add-to-cart-button.module.css";

interface AddToCartButtonProps {
  conversationId: string;
  message: CartMessageType;
  readOnly?: boolean;
  addToCartHandler?: (params: {
    conversationId: string;
    variantId: number;
    quantity: number;
    actionOriginMessageId: string;
    productTitle?: string;
    productId: string;
  }) => Promise<void>;
}

const AddToCartPendingState = memo(function AddToCartPendingState({
  conversationId,
  message,
  readOnly = false,
  addToCartHandler,
}: AddToCartButtonProps) {
  const handleClick = useHandler(async () => {
    if (
      readOnly ||
      message.toolCallId ||
      !message.structuredContent ||
      !addToCartHandler
    ) {
      return;
    }

    await addToCartHandler({
      conversationId,
      variantId: message.structuredContent.variantId,
      quantity: message.structuredContent.quantity,
      actionOriginMessageId: message._id,
      productTitle: message.structuredContent.productTitle,
      productId: message.structuredContent.productId.toString(),
    });
  });

  const truncateText = (text: string, maxLength: number, suffix: string) => {
    const availableLength = maxLength - suffix.length;

    if (text.length <= availableLength) {
      return text + suffix;
    }

    let truncatedText = text.slice(0, availableLength - 3);
    truncatedText = truncatedText.trimEnd();

    return truncatedText + "…" + suffix;
  };

  const buttonText = truncateText(
    `Add ${
      (message.structuredContent?.quantity ?? 0) > 1
        ? `${message.structuredContent?.quantity} `
        : ""
    }${message.structuredContent?.productTitle ?? "product"}`,
    40,
    " to cart",
  );

  return (
    <RedoButton
      className={addToCartButtonCss.restrictedWidthButton}
      disabled={!!message.toolCallId || readOnly || !addToCartHandler}
      hierarchy="primary"
      IconTrailing={ShoppingCartOutlinedIcon}
      onClick={handleClick}
      size="sm"
      text={buttonText}
    />
  );
});

export const AddToCartButton = memo(function AddToCartButton(
  props: AddToCartButtonProps,
) {
  const { message, conversationId, readOnly, addToCartHandler } = props;
  const productTitle = message.structuredContent?.productTitle ?? "item";
  const quantity = message.structuredContent?.quantity ?? 1;

  const { title: confirmedTitle, subtitle: confirmedSubtitle } =
    getConfirmedStateContent(
      message,
      `${productTitle} added to cart`,
      "Unable to add product to cart.",
      `${quantity} ${productTitle} added to your cart`,
    );

  return (
    <ChatContentRenderer
      contentByState={{
        pending: (
          <AddToCartPendingState
            addToCartHandler={addToCartHandler}
            conversationId={conversationId}
            message={message}
            readOnly={readOnly}
          />
        ),
        confirmed: (
          <GenericConfirmedState
            subtitle={confirmedSubtitle}
            title={confirmedTitle}
          />
        ),
        rejected: (
          <GenericRejectedState
            subtitle="Please try again or contact support"
            title="Unable to add to cart"
          />
        ),
      }}
      message={message}
    />
  );
});
